import { useState } from 'react'
import {Calendar, Clock, Users, Zap, LogIn} from 'lucide-react'
import RosterCalculator from '@/components/RosterCalculator'
import FeatureSection from '@/components/FeatureSection'
import FAQSection from '@/components/FAQSection'
import TemplateSidebar from '@/components/TemplateSidebar'
import AuthModal from '@/components/AuthModal'
import UserProfile from '@/components/UserProfile'
import { useAuth } from '@/hooks/useAuth'

function App() {
  const [selectedTemplate, setSelectedTemplate] = useState<{daysOn: number, daysOff: number} | null>(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const { isAuthenticated } = useAuth()

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 text-teal-400" />
              <h1 className="text-xl font-bold">RosterCalculator.com</h1>
            </div>
            <div className="flex items-center gap-6">
              <div className="hidden md:flex gap-6">
                <a href="#calculator" className="hover:text-teal-400 transition-colors">Calculator</a>
                <a href="#features" className="hover:text-teal-400 transition-colors">Features</a>
                <a href="#faq" className="hover:text-teal-400 transition-colors">FAQ</a>
              </div>
              {isAuthenticated ? (
                <UserProfile />
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Shift Roster Calculator & Schedule Planner
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
            Free online roster calculator for shift workers, FIFO employees, and HR managers. 
            Calculate your rotation patterns including 8-6-7-7 roster and 9-5 schedules instantly.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-400" />
              <span>Instant Calculation</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-400" />
              <span>FIFO & HR Friendly</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-teal-400" />
              <span>Complex Patterns</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <TemplateSidebar onSelectTemplate={setSelectedTemplate} />
          </aside>

          {/* Calculator */}
          <main className="flex-1">
            <RosterCalculator selectedTemplate={selectedTemplate} />
          </main>
        </div>
      </div>

      {/* Features Section */}
      <FeatureSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400">
            © 2024 RosterCalculator.com - Professional shift roster calculator online
          </p>
          <p className="text-sm text-slate-500 mt-2">
            Supporting 8-6-7-7 roster calculator, 9-5 roster patterns, and custom rotations
          </p>
        </div>
      </footer>
    </div>
  )
}

export default App
